import type { ChecklistLayoutVersion } from '../types/checklistBooks'
import type { ChecklistItem, ChecklistNodeLink } from '../types/schema'

type SaveGraphNode = Pick<
  ChecklistItem,
  'id' | 'title' | 'prerequisites' | 'gateInputIds' | 'nodeKind' | 'position'
>

export type ReciprocalChecklistLinks = {
  blockedBy: ChecklistNodeLink[]
  blockerFor: ChecklistNodeLink[]
}

const isGateNode = (node: Pick<SaveGraphNode, 'nodeKind'> | null | undefined): boolean =>
  node?.nodeKind === 'GATE_AND' || node?.nodeKind === 'GATE_OR'

const emptyReciprocalLinks = (): ReciprocalChecklistLinks => ({
  blockedBy: [],
  blockerFor: [],
})

export const buildReciprocalDependencyLinks = (
  snapshot: readonly SaveGraphNode[],
): Map<string, ReciprocalChecklistLinks> => {
  const nodeLookup = new Map(snapshot.map((node) => [node.id, node]))
  const reciprocalLinks = new Map(snapshot.map((node) => [node.id, emptyReciprocalLinks()] as const))

  snapshot.forEach((target) => {
    if (isGateNode(target)) return
    target.prerequisites.forEach((sourceId) => {
      const source = nodeLookup.get(sourceId)
      if (!source) return
      if (isGateNode(source)) {
        ;(source.gateInputIds ?? []).forEach((gateInputId) => {
          const gateInput = nodeLookup.get(gateInputId)
          if (!gateInput || isGateNode(gateInput)) return
          reciprocalLinks.get(target.id)?.blockedBy.push({
            targetId: gateInput.id,
            targetType: 'CHECKLIST',
            title: gateInput.title,
          })
          reciprocalLinks.get(gateInput.id)?.blockerFor.push({
            targetId: target.id,
            targetType: 'CHECKLIST',
            title: target.title,
          })
        })
        return
      }
      reciprocalLinks.get(target.id)?.blockedBy.push({
        targetId: source.id,
        targetType: 'CHECKLIST',
        title: source.title,
      })
      reciprocalLinks.get(source.id)?.blockerFor.push({
        targetId: target.id,
        targetType: 'CHECKLIST',
        title: target.title,
      })
    })
  })

  return reciprocalLinks
}

export const buildChecklistBookLayoutEdit = (
  existing: Pick<ChecklistLayoutVersion, 'nodes' | 'wires'> | null | undefined,
  nodesForType: readonly Pick<SaveGraphNode, 'id' | 'position' | 'prerequisites' | 'gateInputIds'>[],
): Pick<ChecklistLayoutVersion, 'nodes' | 'wires'> => {
  const baseNodes = existing?.nodes ?? {}
  const nextNodes: ChecklistLayoutVersion['nodes'] = { ...baseNodes }
  const nodeIdSet = new Set(nodesForType.map((node) => node.id))

  nodesForType.forEach((node) => {
    nextNodes[node.id] = { checklistId: node.id, position: node.position }
  })

  const derivedWires: ChecklistLayoutVersion['wires'] = []
  nodesForType.forEach((node) => {
    ;(node.prerequisites ?? []).forEach((sourceId) => {
      derivedWires.push({ from: sourceId, to: node.id, kind: 'dependency' })
    })
    ;(node.gateInputIds ?? []).forEach((sourceId) => {
      derivedWires.push({ from: sourceId, to: node.id, kind: 'dependency' })
    })
  })

  const preservedWires = (existing?.wires ?? []).filter((wire) => !nodeIdSet.has(wire.from) && !nodeIdSet.has(wire.to))
  return {
    nodes: nextNodes,
    wires: [...preservedWires, ...derivedWires],
  }
}
